import * as THREE from "three"
import * as CANNON from "cannon-es"
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js"
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js"

// ---------------------------------------------------------------------------
// Standalone, embeddable 3D dice roller (three.js + cannon-es).
//
// Usage:
//   const dice = new DicePhysics(containerEl, { numDice: 2 })
//   dice.onRollComplete = (values) => { ... }   // values: number[] face-up
//   dice.roll()                                  // throw the dice
//   dice.dispose()                               // tear down (RAF, GL, listeners)
//
// The dice land on RANDOM faces (physics-driven). The consumer is responsible
// for displaying any authoritative result it cares about — this component only
// renders the throw.
// ---------------------------------------------------------------------------

// Face value layout for a BoxGeometry. Material order is [ +X,-X,+Y,-Y,+Z,-Z ];
// opposite faces sum to 7 (standard die).
const FACE_VALUES = [1, 6, 2, 5, 3, 4]

const FACE_NORMALS = [
  new THREE.Vector3(1, 0, 0),
  new THREE.Vector3(-1, 0, 0),
  new THREE.Vector3(0, 1, 0),
  new THREE.Vector3(0, -1, 0),
  new THREE.Vector3(0, 0, 1),
  new THREE.Vector3(0, 0, -1),
]

// Two in-plane axes per face (same order as FACE_NORMALS / FACE_VALUES).
const FACE_TANGENTS = [
  [new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 1, 0)],
  [new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 1, 0)],
  [new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 0, 1)],
  [new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 0, 1)],
  [new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 1, 0)],
  [new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 1, 0)],
]

// 3x3 grid of pip slots per value.
const PIP_LAYOUT = {
  1: [[1, 1]],
  2: [
    [0, 0],
    [2, 2],
  ],
  3: [
    [0, 0],
    [1, 1],
    [2, 2],
  ],
  4: [
    [0, 0],
    [2, 0],
    [0, 2],
    [2, 2],
  ],
  5: [
    [0, 0],
    [2, 0],
    [1, 1],
    [0, 2],
    [2, 2],
  ],
  6: [
    [0, 0],
    [0, 1],
    [0, 2],
    [2, 0],
    [2, 1],
    [2, 2],
  ],
}

const GROUP_FLOOR = 1
const GROUP_DIE = 2

function rand(min, max) {
  return min + Math.random() * (max - min)
}

// ---------------------------------------------------------------------------
// Deterministic rolling
//
// cannon-es is deterministic given an identical initial state stepped with a
// fixed timestep. The two dice DO collide with each other and the walls (so a
// real, lively roll never leaves them overlapping). To make that deterministic
// we record the WHOLE two-dice throw: at startup we run headless throws and
// bucket the initial state pair by its outcome (valueA, valueB). To show a
// server roll like [2,1] we replay one recorded pair for that outcome — the
// full physics (bounces, collisions) then reproduces both values exactly.
// Recipes are generated and replayed on the same device, so floating-point
// differences across machines never matter.
// ---------------------------------------------------------------------------
const FIXED_STEP = 1 / 120
const MAX_SIM_STEPS = 600 // ~5s cap per headless throw
// A die counts as cleanly landed only if its top face is within ~8° of up.
const FLAT_THRESHOLD = Math.cos((8 * Math.PI) / 180)

// Physics constants shared by the live world and the headless recorder so a
// recorded recipe reproduces exactly during playback. Restitution gives a
// lively bounce; dice collide with each other + walls so they never overlap.
const PHYSICS = {
  gravity: -38,
  floorFriction: 0.5,
  floorRestitution: 0.4,
  dieFriction: 0.4,
  dieRestitution: 0.2,
  wallFriction: 0.4,
  wallRestitution: 0.2,
  mass: 1,
  linearDamping: 0.06,
  angularDamping: 0.1,
  sleepSpeedLimit: 0.15,
  sleepTimeLimit: 0.25,
  spin: 11,
}

const _UP = new THREE.Vector3(0, 1, 0)
const _tmpQuat = new THREE.Quaternion()
const _tmpVec = new THREE.Vector3()

/** Value of the upward face for a quaternion-like {x,y,z,w}. */
function topValueOf(q) {
  _tmpQuat.set(q.x, q.y, q.z, q.w)
  let best = -Infinity
  let value = 1
  for (let i = 0; i < FACE_NORMALS.length; i++) {
    const d = _tmpVec.copy(FACE_NORMALS[i]).applyQuaternion(_tmpQuat).dot(_UP)
    if (d > best) {
      best = d
      value = FACE_VALUES[i]
    }
  }
  return value
}

/** How squarely the up-most face points at the sky (1 = perfectly flat). */
function topAlignmentOf(q) {
  _tmpQuat.set(q.x, q.y, q.z, q.w)
  let best = -Infinity
  for (const n of FACE_NORMALS) best = Math.max(best, _tmpVec.copy(n).applyQuaternion(_tmpQuat).dot(_UP))
  return best
}

/** A cannon world matching the live one (floor + walls + collisions). */
function buildWorld(boundary) {
  const world = new CANNON.World({ gravity: new CANNON.Vec3(0, PHYSICS.gravity, 0) })
  world.broadphase = new CANNON.NaiveBroadphase()
  world.allowSleep = true

  const floorMat = new CANNON.Material("floor")
  const dieMat = new CANNON.Material("die")
  const wallMat = new CANNON.Material("wall")
  world.addContactMaterial(
    new CANNON.ContactMaterial(floorMat, dieMat, { friction: PHYSICS.floorFriction, restitution: PHYSICS.floorRestitution })
  )
  world.addContactMaterial(
    new CANNON.ContactMaterial(dieMat, dieMat, { friction: PHYSICS.dieFriction, restitution: PHYSICS.dieRestitution })
  )
  world.addContactMaterial(
    new CANNON.ContactMaterial(wallMat, dieMat, { friction: PHYSICS.wallFriction, restitution: PHYSICS.wallRestitution })
  )

  const floor = new CANNON.Body({
    type: CANNON.Body.STATIC,
    shape: new CANNON.Plane(),
    material: floorMat,
    collisionFilterGroup: GROUP_FLOOR,
    collisionFilterMask: GROUP_DIE,
  })
  floor.quaternion.setFromEuler(-Math.PI / 2, 0, 0)
  world.addBody(floor)

  for (const [nx, nz] of [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ]) {
    const wall = new CANNON.Body({
      type: CANNON.Body.STATIC,
      shape: new CANNON.Plane(),
      material: wallMat,
      collisionFilterGroup: GROUP_FLOOR,
      collisionFilterMask: GROUP_DIE,
    })
    wall.quaternion.setFromVectors(new CANNON.Vec3(0, 0, 1), new CANNON.Vec3(nx, 0, nz))
    wall.position.set(-nx * boundary, 0, -nz * boundary)
    world.addBody(wall)
  }
  return { world, dieMat }
}

/** A die rigid body that collides with the floor, walls AND the other die. */
function makeDieBody(dieMat, dieSize) {
  const body = new CANNON.Body({
    mass: PHYSICS.mass,
    shape: new CANNON.Box(new CANNON.Vec3(dieSize, dieSize, dieSize)),
    material: dieMat,
    collisionFilterGroup: GROUP_DIE,
    collisionFilterMask: GROUP_FLOOR | GROUP_DIE,
    allowSleep: true,
    linearDamping: PHYSICS.linearDamping,
    angularDamping: PHYSICS.angularDamping,
  })
  body.sleepSpeedLimit = PHYSICS.sleepSpeedLimit
  body.sleepTimeLimit = PHYSICS.sleepTimeLimit
  return body
}

/** A lively random throw at a given lane: tumble + bounce + scatter. */
function randomThrowState(laneX, dropHeight) {
  _tmpQuat.setFromEuler(new THREE.Euler(rand(0, Math.PI * 2), rand(0, Math.PI * 2), rand(0, Math.PI * 2)))
  return {
    p: [laneX + rand(-0.15, 0.15), dropHeight + rand(0, 1.2), rand(-0.3, 0.3)],
    q: [_tmpQuat.x, _tmpQuat.y, _tmpQuat.z, _tmpQuat.w],
    v: [rand(-0.6, 0.6), rand(-2.5, -1), rand(-0.6, 0.6)],
    a: [rand(-PHYSICS.spin, PHYSICS.spin), rand(-PHYSICS.spin, PHYSICS.spin), rand(-PHYSICS.spin, PHYSICS.spin)],
  }
}

/** Drop a recorded throw state (absolute position) onto a cannon body. */
function applyStateToBody(body, state) {
  body.position.set(state.p[0], state.p[1], state.p[2])
  body.quaternion.set(state.q[0], state.q[1], state.q[2], state.q[3])
  body.velocity.set(state.v[0], state.v[1], state.v[2])
  body.angularVelocity.set(state.a[0], state.a[1], state.a[2])
  body.force.setZero()
  body.torque.setZero()
  body.wakeUp()
}

const _emptyPairBuckets = () => {
  const b = {}
  for (let i = 1; i <= 6; i++) {
    b[i] = {}
    for (let j = 1; j <= 6; j++) b[i][j] = []
  }
  return b
}

/**
 * Headless recorder: throw two dice (lanes ±laneSpacing/2) until every outcome
 * pair (valueA, valueB) has `perPair` recorded throws. Time-sliced across
 * animation frames via setTimeout so it never blocks/freezes the UI. Returns a
 * Promise of the bucket: bucket[valueA][valueB] = [{ stA, stB }, ...].
 */
function generatePairRecipes(dieSize, boundary, dropHeight, laneSpacing, perPair = 3, maxTrials = 6000) {
  return new Promise((resolve) => {
    const { world, dieMat } = buildWorld(boundary)
    const a = makeDieBody(dieMat, dieSize)
    const b = makeDieBody(dieMat, dieSize)
    world.addBody(a)
    world.addBody(b)
    const laneA = -laneSpacing / 2
    const laneB = laneSpacing / 2

    const bucket = _emptyPairBuckets()
    const needMore = () => {
      for (let i = 1; i <= 6; i++) for (let j = 1; j <= 6; j++) if (bucket[i][j].length < perPair) return true
      return false
    }

    let trials = 0
    const CHUNK = 12 // trials per time-slice (keeps each tick short)
    const runChunk = () => {
      for (let c = 0; c < CHUNK && trials < maxTrials && needMore(); c++) {
        trials++
        const stA = randomThrowState(laneA, dropHeight)
        const stB = randomThrowState(laneB, dropHeight)
        applyStateToBody(a, stA)
        applyStateToBody(b, stB)
        for (let s = 0; s < MAX_SIM_STEPS; s++) {
          world.step(FIXED_STEP)
          if (a.sleepState === CANNON.Body.SLEEPING && b.sleepState === CANNON.Body.SLEEPING) break
        }
        if (topAlignmentOf(a.quaternion) > FLAT_THRESHOLD && topAlignmentOf(b.quaternion) > FLAT_THRESHOLD) {
          const va = topValueOf(a.quaternion)
          const vb = topValueOf(b.quaternion)
          if (bucket[va][vb].length < perPair) bucket[va][vb].push({ stA, stB })
        }
      }
      if (trials < maxTrials && needMore()) setTimeout(runChunk, 0)
      else resolve(bucket)
    }
    runChunk()
  })
}

// Pair recipes depend only on the physics + geometry, so cache the Promise
// across DicePhysics instances (the small & full dice boxes share one).
const _pairCache = new Map()
function getPairRecipes(dieSize, boundary, dropHeight, laneSpacing) {
  const key = `${dieSize}|${boundary}|${dropHeight}|${laneSpacing}`
  if (!_pairCache.has(key)) _pairCache.set(key, generatePairRecipes(dieSize, boundary, dropHeight, laneSpacing))
  return _pairCache.get(key)
}

// Tone mapping controls how rendered colors are compressed for display. ACES
// (the default) looks cinematic but visibly desaturates saturated colors toward
// pastel. "neutral" (Khronos PBR Neutral) keeps colors punchy while still taming
// highlights; "none" is the most saturated/literal but can clip bright spots.
const TONE_MAPPINGS = {
  none: THREE.NoToneMapping,
  linear: THREE.LinearToneMapping,
  reinhard: THREE.ReinhardToneMapping,
  cineon: THREE.CineonToneMapping,
  aces: THREE.ACESFilmicToneMapping,
  neutral: THREE.NeutralToneMapping,
}

export class DicePhysics {
  /**
   * @param {HTMLElement} container - element the canvas is appended into; the
   *   renderer tracks its size.
   * @param {object} [options]
   * @param {number} [options.numDice=2]
   * @param {number} [options.dieSize=1]      half-extent of the cube
   * @param {number} [options.dropHeight=7]
   * @param {number} [options.laneSpacing=2.4]
   * @param {number} [options.boundary=6]     half-size of the invisible walls
   * @param {number} [options.bodyColor=0xf4f0e6]
   * @param {number} [options.pipColor=0x14171f]
   */
  constructor(container, options = {}) {
    if (!container) throw new Error("DicePhysics: container element is required")
    this.container = container

    this.numDice = options.numDice ?? 2
    this.dieSize = options.dieSize ?? 1
    this.dropHeight = options.dropHeight ?? 7
    this.laneSpacing = options.laneSpacing ?? 2.4
    this.boundary = options.boundary ?? 6
    this.bodyColor = options.bodyColor ?? 0xf4f0e6
    this.pipColor = options.pipColor ?? 0x14171f
    // Unlit mode: render the body & pips as their EXACT colors — no lighting,
    // reflections, tone mapping or any other modification applied. The hex you
    // pass is exactly what you see. (All the material options below are ignored
    // in this mode.) Set false for the lit, glossy resin look.
    this.unlit = options.unlit ?? false
    // Body material style: "plastic" (default, glossy resin), "wood" (matte
    // procedural wood grain), or "marble" (polished marble with veins, like
    // dice-box's marble theme). Wood/marble are tinted with bodyColor. Ignored
    // when unlit.
    this.material = options.material ?? "plastic"
    // Vein/secondary color for the "marble" material (the streaks running
    // through bodyColor). Defaults to white, like a classic red marble.
    this.veinColor = options.veinColor ?? 0xffffff
    // How strongly the studio environment reflects off the body. High values
    // look glossy on a dark background but wash saturated colors toward white
    // on a light background — lower it (e.g. 0.4) for vivid dice on a bright
    // table.
    this.envMapIntensity = options.envMapIntensity ?? 0.9
    // Body finish. Higher roughness + lower clearcoat = a more matte, solid
    // color (less environment reflection, so saturated colors stay saturated
    // instead of washing toward white). Lower roughness + high clearcoat = the
    // glossy resin look.
    this.bodyRoughness = options.bodyRoughness ?? 0.35
    this.clearcoat = options.clearcoat ?? 0.7
    // Self-illumination: lets the body glow its own color so it stays bright &
    // vivid even on faces the lights don't hit (and independent of reflections).
    this.emissiveColor = options.emissiveColor ?? 0x000000
    this.emissiveIntensity = options.emissiveIntensity ?? 0
    // Tone mapping mode — see TONE_MAPPINGS. Use "neutral"/"none" for vivid,
    // saturated dice; "aces" (default) for a softer cinematic look.
    this.toneMapping = TONE_MAPPINGS[options.toneMapping] ?? THREE.ACESFilmicToneMapping
    // Global tone-mapping exposure (1 = neutral). Higher values brighten and can
    // wash saturated colors.
    this.toneMappingExposure = options.toneMappingExposure ?? 1.0
    // Light intensities. Lower the key light + raise ambient for flatter, more
    // even lighting that keeps the body color close to its set value (a strong
    // key light blows out the lit face, making the color read lighter).
    this.keyLightIntensity = options.keyLightIntensity ?? 1.9
    this.ambientIntensity = options.ambientIntensity ?? 0.25
    this.hemiIntensity = options.hemiIntensity ?? 0.7
    this.hemiSkyColor = options.hemiSkyColor ?? 0xbfc8ff
    this.hemiGroundColor = options.hemiGroundColor ?? 0x20222e
    // Secondary fill light. Keep neutral-white by default to avoid shifting red
    // dice toward pink/blue.
    this.fillLightIntensity = options.fillLightIntensity ?? 0.22
    this.fillLightColor = options.fillLightColor ?? 0xffffff
    // Pip appearance. pips are rendered as flat printed circles on each face
    // (not raised spheres). pipRadius is in die-local units (dieSize === 1 =>
    // face half-extent ~1). Bump metalness/lower roughness for glossy ink;
    // keep metalness 0 for clean matte printed pips.
    this.pipRadius = options.pipRadius ?? 0.12
    this.pipMetalness = options.pipMetalness ?? 0.1
    this.pipRoughness = options.pipRoughness ?? 0.3

    // A die is "flat" when its up-most face is within ~8° of straight up.
    this.flatThreshold = Math.cos((8 * Math.PI) / 180)

    // Callback fired once when a roll settles. Receives number[] of face values.
    this.onRollComplete = null

    // Deterministic rolling: roll(values) replays a pre-recorded throw that
    // lands each die on the requested value. Set false to always tumble random.
    this.deterministic = options.deterministic ?? true

    this.rolling = false
    this._raf = 0
    this._settleCheckedAt = 0
    this._lastNudgeAt = 0
    this._acc = 0 // fixed-step accumulator (deterministic stepping)
    this._clock = new THREE.Clock()
    this._fixedStep = FIXED_STEP

    this._initRenderer()
    this._initScene()
    this._initWorld()
    this._buildSharedAssets()
    this._initDice()

    // Pre-record two-dice throws per outcome pair for deterministic playback.
    // Generated asynchronously (time-sliced) so it never blocks the UI; until
    // ready, roll() falls back to a random tumble.
    this._pairs = null
    if (this.deterministic) {
      getPairRecipes(this.dieSize, this.boundary, this.dropHeight, this.laneSpacing).then((b) => {
        this._pairs = b
      })
    }

    this._resizeObserver = new ResizeObserver(() => this.resize())
    this._resizeObserver.observe(this.container)
    this.resize()

    // Show the dice resting before the first roll.
    this.dice.forEach((die, i) => {
      die.body.position.set(this._laneXFor(i), this.dieSize, 0)
      die.body.quaternion.setFromEuler(rand(0, 6.28), rand(0, 6.28), rand(0, 6.28))
      die.body.sleep()
      die.sync()
    })
    this._renderOnce()
  }

  // -------------------------------------------------------------------------
  _initRenderer() {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
    this.renderer.toneMapping = this.toneMapping
    this.renderer.toneMappingExposure = this.toneMappingExposure
    this.renderer.domElement.style.width = "100%"
    this.renderer.domElement.style.height = "100%"
    this.renderer.domElement.style.display = "block"
    this.container.appendChild(this.renderer.domElement)
  }

  _initScene() {
    this.scene = new THREE.Scene()

    // Soft studio reflections so the dice read as a glossy, premium material.
    this._pmrem = new THREE.PMREMGenerator(this.renderer)
    this.scene.environment = this._pmrem.fromScene(new RoomEnvironment(), 0.04).texture

    // Top-down camera looking straight down.
    this.camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100)
    this.camera.up.set(0, 0, -1)
    this.camera.position.set(0, 20, 0)
    this.camera.lookAt(0, 0, 0)

    this.scene.add(new THREE.HemisphereLight(this.hemiSkyColor, this.hemiGroundColor, this.hemiIntensity))
    this.scene.add(new THREE.AmbientLight(0xffffff, this.ambientIntensity))

    const keyLight = new THREE.DirectionalLight(0xffffff, this.keyLightIntensity)
    keyLight.position.set(5, 13, 6)
    keyLight.castShadow = true
    keyLight.shadow.mapSize.set(2048, 2048)
    keyLight.shadow.radius = 6
    keyLight.shadow.bias = -0.0005
    const sc = keyLight.shadow.camera
    sc.near = 1
    sc.far = 40
    sc.left = -12
    sc.right = 12
    sc.top = 12
    sc.bottom = -12
    this.scene.add(keyLight)

    const fillLight = new THREE.DirectionalLight(this.fillLightColor, this.fillLightIntensity)
    fillLight.position.set(-6, 5, -4)
    this.scene.add(fillLight)

    // Transparent shadow catcher so the host background shows through.
    this.ground = new THREE.Mesh(new THREE.PlaneGeometry(60, 60), new THREE.ShadowMaterial({ opacity: 0.32 }))
    this.ground.rotation.x = -Math.PI / 2
    this.ground.receiveShadow = true
    this.scene.add(this.ground)
  }

  _initWorld() {
    // Shared with the headless recorder so recorded pair recipes reproduce.
    const { world, dieMat } = buildWorld(this.boundary)
    this.world = world
    this._dieMat = dieMat
  }

  _buildSharedAssets() {
    if (this.unlit) {
      // Flat, exact colors — unaffected by lights, environment or tone mapping.
      // toneMapped = false guarantees the literal hex reaches the screen.
      this._bodyMaterial = new THREE.MeshBasicMaterial({ color: this.bodyColor })
      this._pipMaterial = new THREE.MeshBasicMaterial({ color: this.pipColor, side: THREE.DoubleSide })
      this._bodyMaterial.toneMapped = false
      this._pipMaterial.toneMapped = false
    } else if (this.material === "wood") {
      // Matte wooden body: procedural grain texture tinted by bodyColor, used
      // for both color and a faint relief. High roughness + almost no env
      // reflection = a soft, dark wood that contrasts strongly with a light
      // table.
      const grain = this._makeWoodTexture(this.bodyColor)
      this._bodyTexture = grain
      this._bodyMaterial = new THREE.MeshStandardMaterial({
        map: grain,
        bumpMap: grain,
        bumpScale: 0.02,
        roughness: this.bodyRoughness,
        metalness: 0.0,
        envMapIntensity: this.envMapIntensity,
      })
      this._pipMaterial = new THREE.MeshStandardMaterial({
        color: this.pipColor,
        roughness: this.pipRoughness,
        metalness: this.pipMetalness,
        side: THREE.DoubleSide,
      })
    } else if (this.material === "marble") {
      // Polished marble: procedural veining over bodyColor (like dice-box's
      // marble theme). A glossy clearcoat + light bump gives the stone sheen.
      const marble = this._makeMarbleTexture(this.bodyColor, this.veinColor)
      this._bodyTexture = marble
      this._bodyMaterial = new THREE.MeshPhysicalMaterial({
        map: marble,
        bumpMap: marble,
        bumpScale: 0.006,
        roughness: this.bodyRoughness,
        metalness: 0.0,
        clearcoat: this.clearcoat,
        clearcoatRoughness: 0.3,
        envMapIntensity: this.envMapIntensity,
      })
      this._pipMaterial = new THREE.MeshPhysicalMaterial({
        color: this.pipColor,
        roughness: this.pipRoughness,
        metalness: this.pipMetalness,
        clearcoat: 0.4,
        side: THREE.DoubleSide,
      })
    } else if (this.material === "gradient") {
      // Premium solid look: a smooth top-to-bottom gradient of bodyColor
      // (lighter top -> deeper bottom) baked into the surface. The depth is in
      // the texture, not in reflections, so the color can't wash to pink.
      const grad = this._makeGradientTexture(this.bodyColor)
      this._bodyTexture = grad
      this._bodyMaterial = new THREE.MeshPhysicalMaterial({
        map: grad,
        roughness: this.bodyRoughness,
        metalness: 0.0,
        clearcoat: this.clearcoat,
        clearcoatRoughness: 0.3,
        envMapIntensity: this.envMapIntensity,
      })
      this._pipMaterial = new THREE.MeshPhysicalMaterial({
        color: this.pipColor,
        roughness: this.pipRoughness,
        metalness: this.pipMetalness,
        clearcoat: 0.3,
        side: THREE.DoubleSide,
      })
    } else {
      this._bodyMaterial = new THREE.MeshPhysicalMaterial({
        color: this.bodyColor,
        roughness: this.bodyRoughness,
        metalness: 0.0,
        clearcoat: this.clearcoat,
        clearcoatRoughness: 0.28,
        envMapIntensity: this.envMapIntensity,
        emissive: new THREE.Color(this.emissiveColor),
        emissiveIntensity: this.emissiveIntensity,
      })
      this._pipMaterial = new THREE.MeshPhysicalMaterial({
        color: this.pipColor,
        roughness: this.pipRoughness,
        metalness: this.pipMetalness,
        clearcoat: 0.6,
        side: THREE.DoubleSide,
      })
    }
    this._boxGeo = new RoundedBoxGeometry(this.dieSize * 2, this.dieSize * 2, this.dieSize * 2, 5, 0.1)
    // Flat disc used as "printed" pip.
    this._pipGeo = new THREE.CircleGeometry(this.pipRadius, 24)
  }

  /**
   * Smooth vertical gradient of baseColor (lighter at top, deeper at bottom)
   * for a clean premium "resin" look — solid color with subtle depth.
   */
  _makeGradientTexture(baseColor) {
    const size = 128
    const canvas = document.createElement("canvas")
    canvas.width = canvas.height = size
    const ctx = canvas.getContext("2d")

    const hex = new THREE.Color(baseColor).getHexString()
    const r0 = parseInt(hex.slice(0, 2), 16)
    const g0 = parseInt(hex.slice(2, 4), 16)
    const b0 = parseInt(hex.slice(4, 6), 16)
    const shade = (f) =>
      `rgb(${Math.min(255, Math.round(r0 * f))},${Math.min(255, Math.round(g0 * f))},${Math.min(
        255,
        Math.round(b0 * f)
      )})`

    const grad = ctx.createLinearGradient(0, 0, 0, size)
    grad.addColorStop(0, shade(1.18)) // lighter highlight at the top
    grad.addColorStop(0.5, shade(1.0)) // true bodyColor through the middle
    grad.addColorStop(1, shade(0.72)) // deeper shade at the bottom
    ctx.fillStyle = grad
    ctx.fillRect(0, 0, size, size)

    const tex = new THREE.CanvasTexture(canvas)
    tex.colorSpace = THREE.SRGBColorSpace
    return tex
  }

  /**
   * Procedural marble CanvasTexture: bodyColor stone shot through with veins
   * of veinColor, like a polished red marble die.
   */
  _makeMarbleTexture(baseColor, veinColor) {
    const size = 256
    const canvas = document.createElement("canvas")
    canvas.width = canvas.height = size
    const ctx = canvas.getContext("2d")
    const img = ctx.createImageData(size, size)
    const data = img.data

    const toBytes = (c) => {
      const h = new THREE.Color(c).getHexString()
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
    }
    const base = toBytes(baseColor)
    const vein = toBytes(veinColor)

    // Tiled value-noise grid (no per-pixel Math.sin in the hash -> fast).
    const G = 256
    const rnd = new Float32Array(G * G)
    for (let i = 0; i < rnd.length; i++) rnd[i] = Math.random()
    const valueAt = (xi, yi) => rnd[(xi & (G - 1)) + (yi & (G - 1)) * G]
    const smooth = (x, y) => {
      const xi = Math.floor(x)
      const yi = Math.floor(y)
      const xf = x - xi
      const yf = y - yi
      const u = xf * xf * (3 - 2 * xf)
      const v = yf * yf * (3 - 2 * yf)
      const a = valueAt(xi, yi)
      const b = valueAt(xi + 1, yi)
      const c = valueAt(xi, yi + 1)
      const d = valueAt(xi + 1, yi + 1)
      return a * (1 - u) * (1 - v) + b * u * (1 - v) + c * (1 - u) * v + d * u * v
    }
    const fbm = (x, y) => {
      let f = 0
      let amp = 0.5
      let freq = 1
      for (let o = 0; o < 5; o++) {
        f += amp * smooth(x * freq, y * freq)
        freq *= 2
        amp *= 0.5
      }
      return f
    }

    const scale = 5 // marble feature size across a face
    const veinSharpness = 6 // higher = thinner, crisper veins
    const veinStrength = 0.6 // how white the veins get (0..1)
    let p = 0
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const nx = (x / size) * scale
        const ny = (y / size) * scale
        const turb = fbm(nx, ny)
        // Diagonal vein bands warped by turbulence -> organic marble streaks.
        const wave = Math.sin((nx + ny + turb * 3.5) * Math.PI * 1.4)
        const veinAmt = Math.pow((wave + 1) * 0.5, veinSharpness) * veinStrength
        // Subtle cloud mottling on the base stone.
        const cloud = 0.92 + 0.16 * (turb - 0.5)
        for (let ch = 0; ch < 3; ch++) {
          const baseC = Math.min(255, base[ch] * cloud)
          data[p + ch] = baseC + (vein[ch] - baseC) * veinAmt
        }
        data[p + 3] = 255
        p += 4
      }
    }
    ctx.putImageData(img, 0, 0)

    const tex = new THREE.CanvasTexture(canvas)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping
    tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy()
    return tex
  }

  /** Procedural wood-grain CanvasTexture tinted around the given base color. */
  _makeWoodTexture(baseColor) {
    const size = 256
    const canvas = document.createElement("canvas")
    canvas.width = canvas.height = size
    const ctx = canvas.getContext("2d")

    // Work in sRGB bytes so the painted colors match `baseColor` on screen.
    const hex = new THREE.Color(baseColor).getHexString()
    const r0 = parseInt(hex.slice(0, 2), 16)
    const g0 = parseInt(hex.slice(2, 4), 16)
    const b0 = parseInt(hex.slice(4, 6), 16)
    const shade = (f, a = 1) =>
      `rgba(${Math.min(255, Math.round(r0 * f))},${Math.min(255, Math.round(g0 * f))},${Math.min(
        255,
        Math.round(b0 * f)
      )},${a})`

    ctx.fillStyle = shade(1)
    ctx.fillRect(0, 0, size, size)

    // Broad low-frequency bands (planks of slightly different tone).
    for (let y = 0; y < size; y += 1) {
      const band = 0.9 + 0.1 * Math.sin(y * 0.05) + (Math.random() - 0.5) * 0.04
      ctx.fillStyle = shade(band, 0.12)
      ctx.fillRect(0, y, size, 1)
    }

    // Fine vertical grain streaks.
    for (let i = 0; i < 2600; i++) {
      const x = Math.random() * size
      const f = 0.6 + Math.random() * 0.7 // darker & lighter grain lines
      ctx.strokeStyle = shade(f, 0.05 + Math.random() * 0.12)
      ctx.lineWidth = 0.4 + Math.random() * 1.6
      ctx.beginPath()
      ctx.moveTo(x, 0)
      ctx.lineTo(x + (Math.random() - 0.5) * 7, size)
      ctx.stroke()
    }

    const tex = new THREE.CanvasTexture(canvas)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping
    tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy()
    return tex
  }

  _buildDieMesh() {
    const group = new THREE.Group()
    const body = new THREE.Mesh(this._boxGeo, this._bodyMaterial)
    body.castShadow = true
    body.receiveShadow = true
    group.add(body)

    const spread = 0.42
    const surface = this.dieSize
    const pipLift = 0.004 // tiny offset so printed pips stay visible above the face
    const pipNormal = new THREE.Vector3(0, 0, 1)
    for (let i = 0; i < FACE_NORMALS.length; i++) {
      const n = FACE_NORMALS[i]
      const [u, w] = FACE_TANGENTS[i]
      for (const [col, row] of PIP_LAYOUT[FACE_VALUES[i]]) {
        const pip = new THREE.Mesh(this._pipGeo, this._pipMaterial)
        pip.position
          .copy(n)
          .multiplyScalar(surface + pipLift)
          .addScaledVector(u, (col - 1) * spread)
          .addScaledVector(w, (row - 1) * spread)
        pip.quaternion.setFromUnitVectors(pipNormal, n)
        pip.castShadow = false
        pip.receiveShadow = false
        group.add(pip)
      }
    }
    return group
  }

  _initDice() {
    this.dice = Array.from({ length: this.numDice }, () => this._makeDie())
  }

  _makeDie() {
    const mesh = this._buildDieMesh()
    this.scene.add(mesh)

    const body = makeDieBody(this._dieMat, this.dieSize)
    this.world.addBody(body)

    const die = {
      mesh,
      body,
      target: null, // the value this die was asked to land on (deterministic)
      sync: () => {
        mesh.position.copy(body.position)
        mesh.quaternion.copy(body.quaternion)
      },
      topValue: () => this._topValue(mesh),
      isFlat: () => this._topFaceAlignment(mesh) > this.flatThreshold,
      throw: (laneX) => {
        applyStateToBody(body, randomThrowState(laneX, this.dropHeight))
      },
      // Replay a recorded throw (deterministically reproduces its outcome).
      applyRecipe: (state) => applyStateToBody(body, state),
      // Safety net: snap the die so the face with `value` points up.
      forceToValue: (value) => {
        const idx = FACE_VALUES.indexOf(value)
        const targetN = FACE_NORMALS[idx >= 0 ? idx : 0]
        const q = new THREE.Quaternion(body.quaternion.x, body.quaternion.y, body.quaternion.z, body.quaternion.w)
        const worldN = targetN.clone().applyQuaternion(q)
        q.premultiply(new THREE.Quaternion().setFromUnitVectors(worldN, new THREE.Vector3(0, 1, 0)))
        body.quaternion.set(q.x, q.y, q.z, q.w)
        body.velocity.setZero()
        body.angularVelocity.setZero()
        body.position.y = this.dieSize
      },
      nudge: () => {
        body.wakeUp()
        const cx = -body.position.x
        const cz = -body.position.z
        const len = Math.hypot(cx, cz) || 1
        const k = 1.4 / len
        body.velocity.set(cx * k, 2.6, cz * k)
        body.angularVelocity.set(rand(-7, 7), rand(-7, 7), rand(-7, 7))
      },
      forceFlatten: () => {
        const q = new THREE.Quaternion(body.quaternion.x, body.quaternion.y, body.quaternion.z, body.quaternion.w)
        const up = new THREE.Vector3(0, 1, 0)
        let best = -Infinity
        let bestN = FACE_NORMALS[0]
        for (const n of FACE_NORMALS) {
          const d = n.clone().applyQuaternion(q).dot(up)
          if (d > best) {
            best = d
            bestN = n
          }
        }
        const worldTop = bestN.clone().applyQuaternion(q)
        q.premultiply(new THREE.Quaternion().setFromUnitVectors(worldTop, up))
        body.quaternion.set(q.x, q.y, q.z, q.w)
        body.velocity.setZero()
        body.angularVelocity.setZero()
        body.position.y = this.dieSize
      },
    }
    return die
  }

  _topValue(mesh) {
    const q = mesh.quaternion
    const up = new THREE.Vector3(0, 1, 0)
    let best = -Infinity
    let value = 1
    for (let i = 0; i < FACE_NORMALS.length; i++) {
      const d = FACE_NORMALS[i].clone().applyQuaternion(q).dot(up)
      if (d > best) {
        best = d
        value = FACE_VALUES[i]
      }
    }
    return value
  }

  _topFaceAlignment(mesh) {
    const q = mesh.quaternion
    const up = new THREE.Vector3(0, 1, 0)
    let best = -Infinity
    for (const n of FACE_NORMALS) best = Math.max(best, n.clone().applyQuaternion(q).dot(up))
    return best
  }

  _laneXFor(index) {
    return (index - (this.numDice - 1) / 2) * this.laneSpacing
  }

  _allSettled() {
    return this.dice.every(
      (d) =>
        d.body.sleepState === CANNON.Body.SLEEPING ||
        (d.body.velocity.lengthSquared() < 0.02 && d.body.angularVelocity.lengthSquared() < 0.02)
    )
  }

  // -------------------------------------------------------------------------
  /**
   * Throw the dice. Pass `values` (e.g. [2, 1]) to roll deterministically — the
   * dice replay a recorded two-dice throw that lands on exactly that pair (with
   * real bounces/collisions). Without `values` (or before the recipes finish
   * generating) the dice tumble to a random result.
   * Safe to call again only after the previous roll settles.
   */
  roll(values) {
    if (this.rolling) return
    this.rolling = true

    const a = Array.isArray(values) ? values[0] : undefined
    const b = Array.isArray(values) ? values[1] : undefined
    const bucket = this._pairs && a != null && b != null && this._pairs[a] ? this._pairs[a][b] : null

    if (this.dice.length === 2 && bucket && bucket.length) {
      const recipe = bucket[Math.floor(Math.random() * bucket.length)]
      this.dice[0].target = a
      this.dice[1].target = b
      this.dice[0].applyRecipe(recipe.stA)
      this.dice[1].applyRecipe(recipe.stB)
    } else {
      this.dice.forEach((die, i) => {
        die.target = null
        die.throw(this._laneXFor(i))
      })
    }

    this._settleCheckedAt = performance.now()
    this._lastNudgeAt = 0
    this._acc = 0
    this._start()
  }

  _finishRoll() {
    this.rolling = false
    // Guarantee the requested value even if physics drifted (rare): snap any
    // deterministic die that didn't land on its target.
    this.dice.forEach((d) => {
      if (d.target != null && d.topValue() !== d.target) d.forceToValue(d.target)
    })
    this.dice.forEach((d) => d.sync())
    const values = this.dice.map((d) => (d.target != null ? d.target : d.topValue()))
    if (this.onRollComplete) this.onRollComplete(values)
  }

  resize() {
    const w = this.container.clientWidth || 1
    const h = this.container.clientHeight || 1
    this.renderer.setSize(w, h, false)
    this.camera.aspect = w / h
    this.camera.updateProjectionMatrix()
    this._renderOnce()
  }

  _renderOnce() {
    this.renderer.render(this.scene, this.camera)
  }

  _start() {
    if (this._raf) return
    this._clock.getDelta() // reset delta so the first frame isn't a huge jump
    this._animate()
  }

  _stop() {
    if (this._raf) cancelAnimationFrame(this._raf)
    this._raf = 0
  }

  _animate = () => {
    this._raf = requestAnimationFrame(this._animate)
    // Deterministic stepping: advance the world in whole fixed steps only (no
    // variable-substep interpolation), so a recorded recipe reproduces exactly
    // regardless of the render frame rate.
    const dt = Math.min(this._clock.getDelta(), 0.05)
    this._acc += dt
    let steps = 0
    while (this._acc >= this._fixedStep && steps < 8) {
      this.world.step(this._fixedStep)
      this._acc -= this._fixedStep
      steps++
    }
    for (const d of this.dice) d.sync()

    if (this.rolling) {
      const now = performance.now()
      const elapsed = now - this._settleCheckedAt
      if (elapsed > 6000) {
        // Hard cap: snap any ambiguous die flat so the result is always clean.
        this.dice.forEach((d) => {
          if (!d.isFlat()) d.forceFlatten()
        })
        this.dice.forEach((d) => d.sync())
        this._finishRoll()
      } else if (elapsed > 400 && this._allSettled()) {
        const stuck = this.dice.filter((d) => !d.isFlat())
        if (stuck.length === 0) {
          this._finishRoll()
        } else if (now - this._lastNudgeAt > 600) {
          stuck.forEach((d) => d.nudge())
          this._lastNudgeAt = now
        }
      }
    }

    this._renderOnce()

    // Idle: once the throw is done and the dice are asleep, stop the loop to
    // free the GPU. roll()/resize() restart it.
    if (!this.rolling && this._allSettled()) this._stop()
  }

  dispose() {
    this._stop()
    this._resizeObserver?.disconnect()
    this._pmrem?.dispose()
    this._bodyTexture?.dispose()
    this.renderer.dispose()
    if (this.renderer.domElement.parentNode === this.container) {
      this.container.removeChild(this.renderer.domElement)
    }
  }
}

export default DicePhysics
